import { useContext, useState } from "react";
import { DocumentActions, DocumentContext } from "./DocumentDetailPageComponent";
import { Document } from "src/types/Document";
import { DragAndDrop, FileProps, imageFileComponent } from "src/components/shared/DragAndDrop";
import { AppAgentContext } from "src/context/ContextProvider";
import { formatSizeUnits, typeFileComponent } from "src/utils";
import { appActions, DataStates, State } from "src/store";
import { ModalType } from "src/types/Modal";
import iziToast from "izitoast";
import { createFolderService, deleteFileService, getFolderFilesService, uploadFileCompressedService } from "src/services/AssetService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { v4 as uuidv4 } from 'uuid';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "src/components/ui/accordion";
import { upsertDocumentService } from "src/services/DocumentService";

export const DocumentSettingsComponent = () => {
  const [filesComponent, setFilesComponent] = useState<FileProps[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const { state: { session: { ai_agent_config: { file_types = ['pdf'], folders: { documents = ''} }}}, dispatch: dispatchApp } = useContext(AppAgentContext)
  const { state: { document }, dispatch } = useContext(DocumentContext);
  const [values, setValues] = useState({ classification: '', example: ''});

  useAsyncEffect(async() => {
    if(files.length > 0) {
        if(document.getSource().length > 0) {
          const res = await uploadFileCompressedService(document.getSource(), files);
          if(res.success) {
            const itemsKey = typeFileComponent(filesComponent, res.items);
            setFilesComponent(itemsKey);
          }
        } else {
          const resp = await createFolderService(documents, uuidv4());
          if(resp) {
              const folderKey = resp.folder.key;
              const doc = new Document(document);
              doc.setSource(folderKey);
              dispatch(DocumentActions.setDocument(doc));      
              const res = await uploadFileCompressedService(folderKey, files);
              if(res.success) {
                const itemsKey = typeFileComponent(filesComponent, res.items);
                setFilesComponent(itemsKey);
              }
          }
        }
    }
  }, [files]);

  const deleteFileFolder = (file: FileProps) => {
    dispatchApp(appActions.openModal({
      type: ModalType.CONFIRMATION,
      data: {
        title: 'Archivo',
        description: '¿Estas seguro de eliminar el archivo?',
        state: DataStates.DOCUMENT,
        async onConfirm() {
          dispatchApp(appActions.onLoading({type: DataStates.DOCUMENT, status: State.LOADING}));
            const res = await deleteFileService(file.key ?? '0');
            if(res.success) {
              iziToast.success({
                message: 'Archivo Eliminado',
                position: 'topCenter'
              });
              setFilesComponent((prevFiles) => prevFiles.filter(fi => fi.key !== file.key));
              dispatchApp(appActions.onLoading({type: DataStates.DOCUMENT, status: State.SUCCESS}));
              dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
            }
        },
        onClose() {
          dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
        },
      }
    }));
  }

  useAsyncEffect(async() => {
    if(document.getSource().length > 0) {
      const response = await getFolderFilesService(document.getSource());
      if(response.success) {
        const filesUp = response.items.map(item => ({
          key: item.key,
          name: item.name,
          type: item.mimetype,
          size: item.size,
        }));
        // const filesUrl = await urltoFileProps(Agent.url_video, file_types);
        setFilesComponent(filesUp);
      }
    }
  }, []);

  const deleteUrl = (data: string, type: 'classification' | 'example') => {
    const title = type === 'classification' ? 'Clasificación' : 'Ejemplo';
    const desc = type === 'classification' ? 'la' : 'el';
    const urls = type === 'classification' ? document.getContractClassification() : [];
    dispatchApp(appActions.openModal({
      type: ModalType.CONFIRMATION,
      data: {
        title,
        description: `¿Estas seguro de eliminar ${desc} ${title.toLowerCase()}?`,
        state: DataStates.DOCUMENT,
        async onConfirm() {
          dispatchApp(appActions.onLoading({type: DataStates.DOCUMENT, status: State.LOADING}));
          const deleteUrl = urls.filter(url => url !== data);
          const doc = new Document(document);
          switch(type) {
            case "classification":
              doc.setContractClassification(deleteUrl);
              break;
            case "example":
              doc.setContractClassification(deleteUrl);
              break;
          }
          const res = await upsertDocumentService(doc);
          if(res.success) {
            iziToast.success({
              message: type === 'classification' ? 'Clasificación eliminada' : 'Ejemplo eliminado',
              position: 'topCenter'
            });
            dispatch(DocumentActions.setDocument(doc));
            dispatchApp(appActions.onLoading({type: DataStates.DOCUMENT, status: State.SUCCESS}));
            dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
          } else {
            dispatchApp(appActions.onLoading({type: DataStates.DOCUMENT, status: State.ERROR}));
          }
        },
        onClose() {
          dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
        },
      }
    }));
  }

  return (
    <div className="flex gap-5 p-5">
      <div className="w-full">
      <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
        <AccordionItem value="item-1">
          <AccordionTrigger>Nombre y Archivos</AccordionTrigger>
          <AccordionContent>
            <div className="grid gap-1 items-center mb-5">
                <label htmlFor="">Nombre del Documento</label>
                <input
                    type="text"
                    autoComplete={'off'}
                    value={document.name}
                    className="py-2 px-4 w-full rounded border border-gray-400 outline-none"
                    placeholder="Escribe el nombre de tu documento"
                    onKeyDown={(event) => {
                      if(event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                    onChange={(event) => {
                      const regex = /^[a-z0-9]*$/; // Permite texto vacío o alfanumérico
                      if (regex.test(event.target.value)) {
                        const doc = new Document(document);
                        doc.setName(event.target.value);
                        dispatch(DocumentActions.setDocument(doc));
                        // dispatch(AiAgentActions.setAgent({...Agent, name: event.target.value}))
                      }
                    }}
                />
            </div>

            <div className="grid gap-1 items-center mb-3">
                <label htmlFor="">Subir archivos</label>
                <DragAndDrop 
                  fileTypes={file_types}
                  setFilesComponent={setFilesComponent}
                  setFiles={setFiles}
                />
            </div>
            <div className="grid gap-1 items-center mb-5">
              <h2>Archivos Incluidos</h2>
              <div className="flex flex-wrap gap-5 my-2 ">
                    {filesComponent.map((file, index) => (
                        <div key={index} className="relative flex gap-5">
                            <div>
                                {imageFileComponent(file)}
                            </div>

                            <div>
                                <p>{file.name}</p>
                                <p>{formatSizeUnits(file.size)}</p>
                            </div>

                            <div>
                                <span
                                    className="cursor-pointer text-red-400 w-5 h-5 flex justify-center items-center"
                                    onClick={() => deleteFileFolder(file)}
                                >
                                    <FontAwesomeIcon icon={faTimesCircle as any} />
                                </span>
                            </div>
                            
                        </div>
                    ))}
                    {
                      filesComponent.length === 0 && (
                        <p className="text-gray-300">Sin archivos</p>
                      )
                    }
                </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="item-2">
          <AccordionTrigger>Configuración del Texto</AccordionTrigger>
          <AccordionContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-1 items-center mb-5">
                  <label htmlFor="">Tamaño de las partes</label>
                  <input
                      type="number"
                      autoComplete={'off'}
                      value={document.getChunkSize()}
                      className="py-2 px-4 w-full rounded border border-gray-400 outline-none"
                      placeholder="Escribe el tamaño"
                      onKeyDown={(event) => {
                        if(event.key === 'Enter') {
                          event.preventDefault();
                        }
                      }}
                      onChange={(event) => {
                        const doc = new Document(document);
                        doc.setChunkSize(event.target.value);
                        dispatch(DocumentActions.setDocument(doc));
                      }}
                  />
              </div>

              <div className="grid gap-1 items-center mb-5">
                <label htmlFor="">Tamaño del overlap</label>
                <input
                    type="number"
                    autoComplete={'off'}
                    value={document.getChunkOverlap()}
                    className="py-2 px-4 w-full rounded border border-gray-400 outline-none"
                    placeholder="Escribe el tamaño"
                    onKeyDown={(event) => {
                      if(event.key === 'Enter') {
                        event.preventDefault();
                        }
                    }}
                    onChange={(event) => {
                      const doc = new Document(document);
                      doc.setChunkOverlap(event.target.value);
                      dispatch(DocumentActions.setDocument(doc));
                    }}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="item-3">
          <AccordionTrigger>Configuración de Revisión</AccordionTrigger>
          <AccordionContent>
            <div className="grid gap-1 items-center mb-5">
              <label htmlFor="">Reglas para el análisis</label>
              <textarea
                  rows={4}
                  autoComplete={'off'}
                  value={document.getContractRevision()}
                  className="py-2 px-4 w-full rounded border border-gray-400 outline-none resize-none"
                  placeholder="Escribe tus reglas para el análisis"
                  onChange={(event) => {
                    const doc = new Document(document);
                    doc.setContractRevision(event.target.value);
                    dispatch(DocumentActions.setDocument(doc));
                  }}
                />
            </div>

            <div className="grid gap-1 items-center mb-5">
              <label htmlFor="">Clasificación</label>
              <div className="flex gap-2">
                <input
                    type="text"
                    autoComplete={'off'}
                    value={values.classification}
                    className="py-2 px-4 w-full rounded border border-gray-400 outline-none resize-none"
                    placeholder="Sin Riesgo, Poco Riesgoso, Riesgoso"
                    onKeyDown={(event) => {
                      if(event.key === 'Enter') {
                        event.preventDefault();
                        const doc = new Document(document);
                        doc.setContractClassification([...doc.getContractClassification(), values.classification]);
                        dispatch(DocumentActions.setDocument(doc));
                        setValues(prev => ({...prev, classification: ''}));
                        // dispatch(AiAgentActions.setAgent({...Agent, website: '', url_web: [...Agent.url_web, Agent.website]}))
                      }
                    }}
                    onChange={(event) => {
                      setValues(prev => ({...prev, classification: event.target.value}));
                    }}
                />
                <div className="w-20">
                  <button className="flex btn btn-sm h-full items-center" type="button" onClick={() => {
                    const doc = new Document(document);
                    doc.setContractClassification([...doc.getContractClassification(), values.classification]);
                    dispatch(DocumentActions.setDocument(doc));
                    setValues(prev => ({...prev, classification: ''}));
                  }}>
                    + Añadir
                  </button>
                </div>
              </div>
            </div>
            <div className="grid gap-1 items-center mb-5">
              <h2>Clasificaciones Incluidas</h2>
              <div className="flex flex-wrap gap-3 my-2">
                {
                  document.getContractClassification().map((classification, index) => (
                    <div key={index} className="p-2 border border-gray-400 rounded flex gap-4">
                      <p className="text-sm">{classification}</p>
                      <span
                          className="cursor-pointer text-red-400 w-5 h-5 flex justify-center items-center"
                          onClick={() => deleteUrl(classification, 'classification')}
                      >
                          <FontAwesomeIcon icon={faTimesCircle as any} />
                      </span>
                    </div>
                  ))
                }
                {
                  document.getContractClassification().length === 0 && (
                    <p className="text-gray-300">Sin Clasificación</p>
                  )
                }
              </div>
            </div>

            <div className="grid gap-1 items-center mb-5">
              <label htmlFor="">Ejemplo</label>
              <div className="flex gap-2">
                <input
                    type="text"
                    autoComplete={'off'}
                    value={values.example}
                    className="py-2 px-4 w-full rounded border border-gray-400 outline-none resize-none"
                    placeholder=""
                    onKeyDown={(event) => {
                      if(event.key === 'Enter') {
                        event.preventDefault();
                        const doc = new Document(document);
                        doc.setContractExample([...doc.getContractExample(), values.example]);
                        dispatch(DocumentActions.setDocument(doc));
                        setValues(prev => ({...prev, example: ''}));
                        // dispatch(AiAgentActions.setAgent({...Agent, website: '', url_web: [...Agent.url_web, Agent.website]}))
                      }
                    }}
                    onChange={(event) => {
                      setValues(prev => ({...prev, example: event.target.value}));
                    }}
                />
                <div className="w-20">
                  <button className="flex btn btn-sm h-full items-center" type="button" onClick={() => {
                    const doc = new Document(document);
                    doc.setContractExample([...doc.getContractExample(), values.example]);
                    dispatch(DocumentActions.setDocument(doc));
                    setValues(prev => ({...prev, example: ''}));
                  }}>
                    + Añadir
                  </button>
                </div>
              </div>
            </div>
            <div className="grid gap-1 items-center mb-5">
              <h2>Ejemplos Incluidos</h2>
              <div className="flex flex-wrap gap-3 my-2">
                {
                  document.getContractExample().map((example, index) => (
                    <div key={index} className="p-2 border border-gray-400 rounded flex gap-4">
                      <p className="text-sm">{example}</p>
                      <span
                          className="cursor-pointer text-red-400 w-5 h-5 flex justify-center items-center"
                          onClick={() => deleteUrl(example, 'example')}
                      >
                          <FontAwesomeIcon icon={faTimesCircle as any} />
                      </span>
                    </div>
                  ))
                }
                {
                  document.getContractExample().length === 0 && (
                    <p className="text-gray-300">Sin Ejemplos</p>
                  )
                }
              </div>
            </div>

            <div className="grid gap-1 items-center mb-5">
                <label htmlFor="">Threshold</label>
                <input
                    type="number"
                    autoComplete={'off'}
                    value={document.getSentimentThreshold()}
                    className="py-2 px-4 w-full rounded border border-gray-400 outline-none"
                    placeholder="Escribe el Threshold"
                    onKeyDown={(event) => {
                      if(event.key === 'Enter') {
                        event.preventDefault();
                        }
                    }}
                    onChange={(event) => {
                      const doc = new Document(document);
                      doc.setSentimentThreshold(event.target.value);
                      dispatch(DocumentActions.setDocument(doc));
                    }}
                />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      </div>
    </div>
  )
}
